import { useEffect, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { deepEqual as isEqual } from 'fast-equals'

export type DerivedFormValues<Values> = {
    /** Applies values the form derived itself, rather than the user entering them. */
    applyDerivedValues: (patch: Partial<Values>) => void
    /** Put on the element holding the fields; it tells the hook the user started typing. */
    onKeyDownCapture: () => void
}

/**
 * Values a dialog derives after it mounted - a default category from the settings, the rate of the file it is
 * registering on - are not changes the user made, so they should not leave a freshly opened dialog dirty. Applying
 * them with resetForm makes them the new baseline instead (HAM-4275).
 *
 * Two things make this trickier than it looks, and both are the reason this lives in a hook rather than in every
 * dialog:
 *
 * - Several effects run in the same render pass and each would reset from the same, still unpatched values: the last
 *   one would win and the rest would be lost. The patches are therefore collected and applied together.
 * - resetForm rewrites every field from a snapshot. Doing that after a keystroke costs the user that keystroke,
 *   because it had not been rendered yet - and the form then even considers itself unchanged. So from the first key
 *   press onwards the values are written one by one and the form simply counts as changed.
 */
export default function useDerivedFormValues<Values>(): DerivedFormValues<Values> {
    const { values, dirty, setFieldValue, resetForm } = useFormikContext<Values>()

    const pending = useRef<Partial<Values>>({})
    const userEdited = useRef<boolean>(false)
    const [version, setVersion] = useState<number>(0)

    useEffect(() => {
        if (Object.keys(pending.current).length === 0) {
            return
        }

        const patch = pending.current
        pending.current = {}

        const changed = Object.entries(patch).filter(([field, value]) => !isEqual(values[field], value))
        if (changed.length === 0) {
            return
        }

        if (dirty || userEdited.current) {
            changed.forEach(([field, value]) => setFieldValue(field, value))
            return
        }

        resetForm({ values: { ...values, ...patch } })
    }, [version])

    return {
        applyDerivedValues: (patch: Partial<Values>) => {
            pending.current = { ...pending.current, ...patch }
            setVersion(current => current + 1)
        },
        onKeyDownCapture: () => (userEdited.current = true),
    }
}
